import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../../services/useAuth";
import { API_URL } from "../shared/constants";
import { salesMonthLabelTr } from "../shared/utils";

export function useDeliveriesReport() {
  const { getToken } = useAuth();
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [deliveriesData, setDeliveriesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [yearsLoading, setYearsLoading] = useState(true);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );
  const addToast = useCallback(
    (msg, type = "success") =>
      setToasts((p) => [...p, { id: Date.now(), message: msg, type }]),
    [],
  );
  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );

  const fetchData = useCallback(
    async (yr) => {
      const targetYear = yr ?? year;
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/analytics/delivered?year=${targetYear}`,
          authH(),
        );
        setDeliveriesData(r.data);
      } catch {
        addToast("Teslimat verisi yüklenemedi", "danger");
      } finally {
        setLoading(false);
      }
    },
    [year, authH, addToast],
  );

  useEffect(() => {
    (async () => {
      setYearsLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/api/analytics/delivered/years`,
          authH(),
        );
        const available = r.data.years || [];
        setYears(available);
        const current = new Date().getFullYear();
        const initial = available.includes(current)
          ? current
          : (available[0] ?? current);
        setYear(initial);
        await fetchData(initial);
      } catch {
        await fetchData();
      } finally {
        setYearsLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const handleYearChange = useCallback(
    (yr) => {
      setYear(yr);
      fetchData(yr);
    },
    [fetchData],
  );

  const derived = useMemo(() => {
    if (!deliveriesData) return null;
    const monthly = deliveriesData.monthlyDeliveries || [];

    const labels = monthly.map((m, i) => salesMonthLabelTr(i, m.year || year));
    const totalSeries = monthly.map((m) => m.deviceCount || 0);

    const modelMap = {};
    monthly.forEach((m) => {
      (m.models || []).forEach((mod) => {
        if (!modelMap[mod.name]) modelMap[mod.name] = { name: mod.name, count: 0 };
        modelMap[mod.name].count += mod.count || 0;
      });
    });
    const modelRows = Object.values(modelMap).sort((a, b) => b.count - a.count);

    const chartSeries = deliveriesData.chartSeries || [];

    return { labels, totalSeries, modelRows, chartSeries };
  }, [deliveriesData, year]);

  return {
    years,
    year,
    handleYearChange,
    deliveriesData,
    loading: loading || yearsLoading,
    derived,
    toasts,
    dismissToast,
  };
}
